import { ic, sized } from '@/components/Icons';
import { useI18n } from '@/i18n';
import { usePuedeEditar } from '@/hooks/usePuedeEditar';

export interface BannerProps {
  open: boolean;
  deadline: string;
  /** True when nothing in the user's scope is pending. */
  hidden?: boolean;
}

/** La franja de la ventana de actualización.
 *
 *  Antes desaparecía del todo cuando la ventana estaba abierta y el socio
 *  ya lo tenía todo completo: cerrada salía franja, abierta no. Vitalino
 *  lo señaló en la prueba 2.2 y tenía razón en que confunde — la fecha de
 *  cierre sigue importando aunque no quede nada rojo, porque hasta ese día
 *  se puede corregir.
 *
 *  Así que siempre hay franja; lo que cambia es el tono. Naranja mientras
 *  falte algo, verde discreto cuando está todo, gris al cerrar.
 *
 *  ─── y lo que dice depende de quién mira ───
 *
 *  La franja no sabía el rol de nadie, así que con la ventana cerrada
 *  anunciaba «modo solo lectura» a todo el mundo por igual. Para un Admin
 *  ETC eso es falso —escribe siempre, la ventana es un mecanismo para los
 *  socios— y además contradecía a los botones de editar, que a él sí le
 *  responden. Una pantalla que se contradice a sí misma o te paraliza o te
 *  empuja a buscar la vuelta, y aquí la vuelta era abrir la ventana de
 *  verdad, que es una decisión con fecha y aviso a 134 socios.
 *
 *  No se duplica la regla: se le pregunta a `usePuedeEditar`, que es la
 *  misma que usan los botones y el espejo de la que aplica el servidor. */
export function Banner({ open, deadline, hidden }: BannerProps) {
  const { lang, t } = useI18n();
  // El ámbito representativo es el site: es el dato que la ventana gobierna.
  const permiso = usePuedeEditar('site');
  const completo = open && hidden;
  /** Escribe aunque la ventana esté cerrada. Hoy, solo el Admin ETC. */
  const exento = !open && permiso.puede;
  /** Su rol nunca escribe, así que esperar a la próxima ventana no le sirve. */
  const soloConsulta = permiso.permanente;

  const dateStr = new Date(deadline).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  const clave = exento ? 'window.closed_banner_admin'
    : open ? 'window.open_banner'
    : 'window.closed_banner';

  return (
    <div
      style={{
        background: !open ? '#3E4756'
          : completo ? '#1E7A4B'
          : 'linear-gradient(90deg, #F26522 0%, #FF8345 100%)',
        color: '#fff', padding: '10px 24px', fontSize: 13, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      {/* Al exento no se le enseña un candado: para él no hay nada cerrado. */}
      {sized(exento ? ic.info : !open ? ic.lock : completo ? ic.check : ic.bell, 15)}
      <span>
        {t(clave, { date: dateStr })}
        {completo ? (lang === 'es' ? ' · Tus datos están completos' : ' · Your data is complete') : null}
        {soloConsulta ? ` · ${t('window.readonly_role')}` : null}
      </span>
    </div>
  );
}
